import type { OrderStatus, OrderType, PaymentMethod, PaymentStatus, ReservationStatus, TeamMember, UserRole } from '../types';

type BackendOrderStatus = 'AWAITING_PAYMENT' | 'CONFIRMED' | 'PENDING' | 'PREPARING' | 'READY' | 'DELIVERED' | 'CANCELED';
type BackendOrderType = 'LOCAL' | 'DELIVERY' | 'PICKUP';
type BackendPaymentMethod = 'PIX' | 'MERCADO_PAGO' | 'CARD_ON_DELIVERY' | 'CASH';
type BackendPaymentStatus = 'PENDING' | 'PAID' | 'FAILED' | 'EXPIRED';
type BackendUserRole = 'SUPER_ADMIN' | 'ADMIN' | 'STAFF';

const orderStatusMap: Record<BackendOrderStatus, OrderStatus> = {
    AWAITING_PAYMENT: 'awaiting_payment',
    CONFIRMED: 'confirmed',
    PENDING: 'pending',
    PREPARING: 'preparing',
    READY: 'ready',
    DELIVERED: 'delivered',
    CANCELED: 'cancelled',
};

const orderTypeMap: Record<BackendOrderType, OrderType> = {
    LOCAL: 'table',
    DELIVERY: 'delivery',
    PICKUP: 'counter',
};

const paymentMethodMap: Record<BackendPaymentMethod, PaymentMethod> = {
    PIX: 'pix',
    MERCADO_PAGO: 'mercado_pago',
    CARD_ON_DELIVERY: 'card_on_delivery',
    CASH: 'cash',
};

const paymentStatusMap: Record<BackendPaymentStatus, PaymentStatus> = {
    PENDING: 'pending',
    PAID: 'paid',
    FAILED: 'failed',
    EXPIRED: 'expired',
};

const userRoleMap: Record<BackendUserRole, UserRole> = {
    SUPER_ADMIN: 'platform',
    ADMIN: 'owner',
    STAFF: 'attendant',
};

export const toFrontendOrderStatus = (status: string): OrderStatus =>
    orderStatusMap[status as BackendOrderStatus] ?? 'pending';

export const toBackendOrderStatus = (status: OrderStatus): BackendOrderStatus => {
    switch (status) {
        case 'awaiting_payment':
            return 'AWAITING_PAYMENT';
        case 'confirmed':
            return 'CONFIRMED';
        case 'pending':
            return 'PENDING';
        case 'preparing':
            return 'PREPARING';
        case 'ready':
            return 'READY';
        case 'delivered':
            return 'DELIVERED';
        case 'cancelled':
            return 'CANCELED';
        default:
            return 'PENDING';
    }
};

export const toFrontendOrderType = (type: string): OrderType =>
    orderTypeMap[type as BackendOrderType] ?? 'counter';

export const toFrontendPaymentMethod = (method?: string | null): PaymentMethod | undefined =>
    method ? paymentMethodMap[method as BackendPaymentMethod] : undefined;

export const toFrontendPaymentStatus = (status?: string | null): PaymentStatus | undefined =>
    status ? paymentStatusMap[status as BackendPaymentStatus] : undefined;

export const toFrontendUser = (user: any): TeamMember => ({
    id: user.id,
    establishmentId: user.establishmentId ?? undefined,
    name: user.name ?? user.email,
    email: user.email,
    role: userRoleMap[user.role as BackendUserRole] ?? 'owner',
    subscriptionRestricted: !!user.subscriptionRestricted,
    active: true,
    createdAt: new Date().toISOString(),
});

export const normalizeOrder = (order: any) => ({
    ...order,
    status: toFrontendOrderStatus(order.status),
    type: toFrontendOrderType(order.type),
    paymentMethod: toFrontendPaymentMethod(order.paymentMethod),
    paymentStatus: toFrontendPaymentStatus(order.paymentStatus),
    items: (order.items ?? []).map((item: any) => ({
        ...item,
        productName: item.product?.name ?? item.productName,
        subtotal: item.subtotal ?? item.price * item.quantity,
    })),
});

export const toFrontendReservationStatus = (status: string): ReservationStatus => {
    switch (status) {
        case 'CONFIRMED':
            return 'confirmed';
        case 'CANCELED':
            return 'cancelled';
        case 'COMPLETED':
            return 'completed';
        case 'PENDING':
        default:
            return 'pending';
    }
};

export const toBackendReservationStatus = (status: ReservationStatus): 'PENDING' | 'CONFIRMED' | 'CANCELED' | 'COMPLETED' => {
    switch (status) {
        case 'confirmed':
            return 'CONFIRMED';
        case 'cancelled':
            return 'CANCELED';
        case 'completed':
            return 'COMPLETED';
        default:
            return 'PENDING';
    }
};

export const normalizeReservation = (reservation: any) => ({
    ...reservation,
    status: toFrontendReservationStatus(reservation.status),
    time: reservation.time ?? new Date(reservation.date).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
    }),
    partySize: reservation.partySize ?? reservation.guests,
});
